/**
 * this is Order Confirmation Summary items : Your Items
 *
 * @module views/components/OrderConfirmationSummary
 * @memberof -Common
 */
import './OrderConfirmationSummary.scss';

import React from 'react';

import PropTypes from 'prop-types';

import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import RailTitle from '@ulta/components/RailTitle/RailTitle';

import SummaryProductCard from '../SummaryProductCard/SummaryProductCard';

/**
 * Represents a OrderConfirmationSummary component
 *
 * @method
 * @param {OrderConfirmationSummaryProps} props - React properties passed from composition
 * @returns OrderConfirmationSummary
 */

export const OrderConfirmationSummary = function( props ){
  const {
    orderSummaryTitle,
    countLabel,
    bagItems,
    giftWithPurchaseTitle,
    giftItems,
    giftCountLabel,
    componentKey,
    trackAction,
    splitCount
  } = props;

  const hasBagItems = hasItems( bagItems );
  const hasGifts = hasItems( giftItems );

  if( !hasBagItems && !hasGifts ){
    return null;
  }

  return (
    <div className='OrderConfirmationSummary'>
      <RailTitle
        title={ orderSummaryTitle }
        subTitle={ countLabel }
      />
      <div className='OrderConfirmationSummary__PackageNumberWithTrackingDetails'>
        <div className='OrderConfirmationSummary__ShipmentNumberData'>
          <Text
            textStyle='body-3'
            htmlTag='span'
          >
            { splitCount }
          </Text>
        </div>
        { trackAction?.url && (
          <div className='OrderConfirmationSummary__TrackingNumber'>
            <Link_Huge
              compact
              ariaLabel={ trackAction.label }
              action={ trackAction }
              target='_self'
            >
              { trackAction.label }
            </Link_Huge>
          </div>
        ) }
      </div>
      { hasBagItems && (
        <div className='OrderConfirmationSummary__bagProductCardItems'>
          { bagItems.map( ( product, productIndex ) => (
            <SummaryProductCard
              key={ `${componentKey}-bag-items-${productIndex}` }
              textColor='neutral-600'
              { ...spreadProps( { ...product } ) }
              ariaLabel='product'
              salePrice={ product.finalPrice }
            />
          ) ) }
        </div>
      ) }

      { hasGifts && (
        <>
          <div className='OrderConfirmationSummary__giftHeading'>
            <Text textStyle='title-5'
              htmlTag='h2'
            >{ giftWithPurchaseTitle }</Text>
          </div>
          <RailTitle
            subTitle={ giftCountLabel }
          />
          <div className='OrderConfirmationSummary__giftWithPurchaseItems'>

            { giftItems.map( ( gift, giftIndex ) => (
              <SummaryProductCard
                ariaLabel='Gift Item'
                key={ `${componentKey}-gift-items-${giftIndex}` }
                textColor='neutral-600'
                { ...gift }
                salePrice={ gift.finalPrice }
              />
            ) ) }

          </div>
        </>
      ) }
    </div>
  );
};

/**
 * Property type definitions
 * @typedef OrderConfirmationSummaryProps
 * @type {object}
 * @property {string} componentKey - Sets the componentKey
 * @property {string} countLabel - Items count Label
 * @property {object} bagItems - Sets bag items object
 * @property {object} giftItems - Sets gift items object
 * @property {string} orderSummaryTitle - Order summary title and label
 * @property {string} giftWithPurchaseTitle - Gift with purchase title and label
 * @property {object} trackAction - Action for tracking url
 * @property {string} splitCount - Split count label for tracking url
 */
export const propTypes = {
  componentKey: PropTypes.string,
  countLabel: PropTypes.string,
  bagItems: PropTypes.arrayOf(
    PropTypes.shape( {
      brandName: PropTypes.string,
      productName: PropTypes.string,
      variantTypeLabel: PropTypes.string,
      variantTypeDesc: PropTypes.string,
      quantityLabel: PropTypes.string,
      image: PropTypes.string,
      finalPrice: PropTypes.string,
      deals: PropTypes.array,
      unitPriceBreakup: PropTypes.string
    } )
  ),
  giftItems: PropTypes.arrayOf(
    PropTypes.shape( {
      brandName: PropTypes.string,
      productName: PropTypes.string,
      variantTypeLabel: PropTypes.string,
      variantTypeDesc: PropTypes.string,
      quantityLabel: PropTypes.string,
      image: PropTypes.string,
      finalPrice: PropTypes.string
    } )
  ),
  orderSummaryTitle: PropTypes.string,
  giftWithPurchaseTitle: PropTypes.string,
  trackAction: PropTypes.object,
  splitCount: PropTypes.string
};

OrderConfirmationSummary.propTypes = propTypes;

export default OrderConfirmationSummary;
